"use client";

import Image from "@/components/ui/imageV2/ImageV2";
import { CLink } from "@/components/common/customLink/CustomLink";
import VisibilitySensor from "@/components/common/VisibilitySensor";
import ExpandableLineClampContent, {
  PortableTextValue,
} from "@/components/common/ExpandableLineClampContent";
import PortableText from "@/components/PortableText";
import { TranslationData } from "@/common/lib/types";
import { PortableTextBlock } from "next-sanity";
import { ImageProps } from "next/image";
import { DetailedHTMLProps, HTMLAttributes } from "react";
import { cn } from "@/lib/tailwind";

export type PromotionalImageCardAction = {
  cta?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  subDescription?: React.ReactNode;
};

export type PromotionalImageCardProps = {
  imageProps: ImageProps;
  containerProps?: Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    "children"
  >;
  action: PromotionalImageCardAction;
  url?: string;
  onClick?: () => void;
  onVisible?: (isVisible: boolean) => void;
  translations?: TranslationData;
  subDescriptionLineClamp?: number;
  linkStyle?: { backgroundColor?: string; color?: string };
  contentBgColor?: string;
};

export default function PromotionalImageCard({
  imageProps,
  containerProps = {},
  action,
  url,
  onClick,
  onVisible,
  translations,
  subDescriptionLineClamp = 6,
  linkStyle,
  contentBgColor = "#F4F4F4",
}: PromotionalImageCardProps) {
  const media = (
    <div className="relative h-full w-full overflow-hidden">
      <Image
        {...imageProps}
        className={cn(imageProps?.className, "h-full w-full object-cover")}
      />
    </div>
  );

  const hasRightSection = action?.subDescription;

  return (
    <div
      {...containerProps}
      className={cn("flex h-full w-full flex-col", containerProps?.className)}
      onClick={onClick}
    >
      {url ? (
        <CLink href={url} className="block h-full w-full">
          {media}
        </CLink>
      ) : (
        media
      )}

      <div
        className={cn(
          "grid gap-6 px-4 py-6 md:px-6 md:py-8",
          hasRightSection ? "md:grid-cols-2" : "grid-cols-1",
        )}
        style={{ backgroundColor: contentBgColor }}
      >
        <div className="flex flex-col">
          {action?.title &&
            (typeof action.title === "string" ? (
              <h2 className="mb-1 text-[0.875rem] font-bold uppercase leading-tight text-black md:mb-2 lg:text-[32px]">
                {action.title}
              </h2>
            ) : (
              action.title
            ))}
          {action?.description && (
            <div className="pt-2 text-sm text-black lg:text-lg">
              <PortableText value={action.description as unknown as PortableTextBlock} />
            </div>
          )}
          {hasRightSection && (
            <div className="md:hidden">
              <ExpandableLineClampContent
                portableTextValue={action.subDescription as unknown as PortableTextValue}
                translations={translations}
                lineClamp={subDescriptionLineClamp}
                className="pt-2 text-sm text-black"
              />
            </div>
          )}
          {action?.cta && (
            <div className="mt-3 sm:mt-4">
              {typeof action?.cta === "string" ? <p>{action?.cta}</p> : action?.cta}
            </div>
          )}
        </div>

        {hasRightSection && (
          <div className="hidden md:block">
            <ExpandableLineClampContent
              portableTextValue={action.subDescription as unknown as PortableTextValue}
              translations={translations}
              lineClamp={subDescriptionLineClamp}
              className="text-sm text-black md:text-base"
            />
          </div>
        )}
      </div>

      {onVisible && (
        <div className="w-full">
          <VisibilitySensor onVisible={onVisible}>
            <div />
          </VisibilitySensor>
        </div>
      )}
    </div>
  );
}
